import { createReducer, on } from '@ngrx/store';
import * as AuthActions from './auth.actions';
import { AuthState } from './auth.state';
import { JwtHelperService } from '@auth0/angular-jwt';

type PortalType = 'administration' | 'user' | 'external' | null;

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  user: null,
  roles: [],
  currentPortal: null,
  error: null,
  isInitialized: false
};

const determinePortal = (roles: string[]): PortalType => {
  if (roles.some((role: string) => ['superadmin', 'orgadmin', 'orgbruger'].includes(role))) {
    return 'administration';
  } else if (roles.some((role: string) => ['uddansvarlig', 'uddassistent', 'bruger'].includes(role))) {
    return 'user';
  } else if (roles.includes('external')) {
    return 'external';
  }
  return null;
};

export const authReducer = createReducer(
  initialState,
  on(AuthActions.resetAllState, () => {
    return {
      ...initialState,
      isInitialized: true // Keep initialized to avoid triggering re-initialization
    };
  }),
  on(AuthActions.initializeAuth, (state) => ({
    ...state,
    isInitialized: false
  })),
  on(AuthActions.initializeAuthSuccess, (state, { token, user }) => {
    const roles = user?.roles || [];
    const portal = determinePortal(roles);
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: !!token,
      token,
      user,
      roles,
      currentPortal: portal,
      error: null
    };
  }),
  on(AuthActions.refreshTokenSuccess, (state, { accessToken }) => {
    // Extract user from token using JwtHelperService
    const jwtHelper = new JwtHelperService();
    try {
      const decoded = jwtHelper.decodeToken(accessToken) as any;
      const roles = Object.entries(decoded.user_roles || {}).map(([role]) => role.toLowerCase());
      const user = {
        id: decoded.users_id,
        uuid: decoded.uuid,
        username: decoded.username,
        roles,
        userInfo: decoded.user_info
      };
      const portal = determinePortal(roles);
      
      return {
        ...state,
        isAuthenticated: true,
        token: accessToken,
        user,
        roles,
        currentPortal: portal,
        error: null
      };
    } catch (error) {
      console.error('[AuthReducer] Error decoding token in refreshTokenSuccess:', error);
      // Fallback to previous behavior if token decoding fails
      const portal = determinePortal(state.roles);
      return {
        ...state,
        isAuthenticated: true,
        token: accessToken,
        currentPortal: portal,
        error: null
      };
    }
  }),
  on(AuthActions.logout, () => ({
    ...initialState,
    isInitialized: true,
    currentPortal: null
  })),
  on(AuthActions.clearState, () => ({
    ...initialState,
    isInitialized: true,
    currentPortal: null
  })),
  on(AuthActions.loginSuccess, (state, { token, user, isAuthenticated }) => {
    const roles = user?.roles || [];
    const portal = determinePortal(roles);
    return {
      ...state,
      token,
      user,
      isAuthenticated,
      roles,
      currentPortal: portal,
      error: null,
      isInitialized: true
    };
  }),
  on(AuthActions.updateToken, (state, { token }) => {
    // Extract user from token using JwtHelperService
    const jwtHelper = new JwtHelperService();
    try {
      const decoded = jwtHelper.decodeToken(token) as any;
      const roles = Object.entries(decoded.user_roles || {}).map(([role]) => role.toLowerCase());
      const user = {
        id: decoded.users_id,
        uuid: decoded.uuid,
        username: decoded.username,
        roles,
        userInfo: decoded.user_info
      };
      const portal = determinePortal(roles);
      
      return {
        ...state,
        token,
        user,
        roles,
        currentPortal: portal,
        isAuthenticated: true,
        error: null
      };
    } catch (error) {
      console.error('[AuthReducer] Error decoding token:', error);
      return state;
    }
  })
);
